/**
 * URL Sources Page
 * Phase 0: URL Crawler & Web Content Indexing
 * v1.2.0 - Added Crawl Selected feature
 *
 * Manage web URLs for crawling and indexing into the knowledge base
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { urlsApi, UrlSource } from '@/services/api/urls';
import {
  Card,
  CardContent,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PageHeader } from '@/components/shared/PageHeader';
import {
  Globe,
  Plus,
  Search,
  RefreshCw,
  Trash2,
  MoreHorizontal,
  ExternalLink,
  FileText,
  Clock,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertCircle,
  Link2,
  Map,
  Link,
  Image,
  FileCode,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';

function StatusBadge({ status }: { status: UrlSource['status'] }) {
  switch (status) {
    case 'indexed':
      return (
        <Badge variant="default" className="bg-green-500">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Indexed
        </Badge>
      );
    case 'crawling':
      return (
        <Badge variant="secondary">
          <Loader2 className="mr-1 h-3 w-3 animate-spin" />
          Crawling
        </Badge>
      );
    case 'pending':
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-600">
          <Clock className="mr-1 h-3 w-3" />
          Pending
        </Badge>
      );
    case 'failed':
      return (
        <Badge variant="destructive">
          <XCircle className="mr-1 h-3 w-3" />
          Failed
        </Badge>
      );
    case 'disabled':
      return (
        <Badge variant="outline" className="text-muted-foreground">
          <AlertCircle className="mr-1 h-3 w-3" />
          Disabled
        </Badge>
      );
    default:
      return <Badge variant="outline">{status}</Badge>;
  }
}

function FrequencyBadge({ frequency }: { frequency: string }) {
  const colors: Record<string, string> = {
    manual: 'bg-gray-100 text-gray-700',
    daily: 'bg-blue-100 text-blue-700',
    weekly: 'bg-purple-100 text-purple-700',
    monthly: 'bg-orange-100 text-orange-700',
  };

  return (
    <Badge variant="outline" className={colors[frequency] || ''}>
      {frequency.charAt(0).toUpperCase() + frequency.slice(1)}
    </Badge>
  );
}

function formatBytes(bytes: number | undefined): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function UrlSourcesPage() {
  // v1.2.0: Crawl Selected feature
  const _version = '1.2.0';
  console.log('UrlSourcesPage version:', _version);
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<UrlSource | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addTab, setAddTab] = useState<'single' | 'bulk' | 'sitemap'>('single');

  // Form state
  const [newUrl, setNewUrl] = useState('');
  const [bulkUrls, setBulkUrls] = useState('');
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [crawlFrequency, setCrawlFrequency] = useState<'manual' | 'daily' | 'weekly' | 'monthly'>('manual');
  const [maxUrls, setMaxUrls] = useState(100);
  const [includePattern, setIncludePattern] = useState('');
  const [excludePattern, setExcludePattern] = useState('');
  const [extractMainContent, setExtractMainContent] = useState(true);
  const [respectRobotsTxt, setRespectRobotsTxt] = useState(true);
  const [includeImages, setIncludeImages] = useState(true);
  const [refreshingUrlId, setRefreshingUrlId] = useState<string | null>(null);
  const [isCrawlingAll, setIsCrawlingAll] = useState(false);
  const [crawlingUrlIds, setCrawlingUrlIds] = useState<Set<string>>(new Set());

  // Fetch URLs - with polling when crawling is in progress
  const { data, isLoading, error } = useQuery({
    queryKey: ['urls', statusFilter],
    queryFn: () => urlsApi.listUrls({
      status: statusFilter !== 'all' ? statusFilter : undefined,
      limit: 100,
    }),
    // Poll every 3 seconds while crawling is in progress
    refetchInterval: isCrawlingAll ? 3000 : false,
  });

  // Check if there are any URLs currently crawling
  const crawlingCount = data?.urls.filter(u => u.status === 'crawling').length || 0;

  // Track when crawling started to give it time to initialize
  const [crawlStartTime, setCrawlStartTime] = useState<number | null>(null);

  // Stop polling when no more URLs are crawling (with a delay to allow backend to start)
  useEffect(() => {
    if (isCrawlingAll) {
      if (!crawlStartTime) {
        setCrawlStartTime(Date.now());
      } else if (crawlingCount === 0) {
        // Give the backend at least 5 seconds to start processing before stopping
        const elapsed = Date.now() - crawlStartTime;
        if (elapsed > 5000) {
          // All done crawling
          setIsCrawlingAll(false);
          setCrawlStartTime(null);
          setCrawlingUrlIds(new Set()); // Clear the crawling IDs
        }
      }
    } else {
      setCrawlStartTime(null);
    }
  }, [crawlingCount, isCrawlingAll, crawlStartTime]);

  // Update crawlingUrlIds when URLs finish crawling (remove indexed URLs from the set)
  useEffect(() => {
    if (crawlingUrlIds.size > 0 && data?.urls) {
      const stillCrawling = new Set<string>();
      for (const urlId of crawlingUrlIds) {
        const url = data.urls.find(u => u.id === urlId);
        // Keep in set if still pending or crawling (not yet indexed/failed)
        if (url && (url.status === 'pending' || url.status === 'crawling')) {
          stillCrawling.add(urlId);
        }
      }
      // Only update if there's a difference
      if (stillCrawling.size !== crawlingUrlIds.size) {
        setCrawlingUrlIds(stillCrawling);
      }
    }
  }, [data?.urls, crawlingUrlIds]);

  // Add single URL mutation
  const addUrlMutation = useMutation({
    mutationFn: urlsApi.addUrl,
    onSuccess: () => {
      toast.success('URL added successfully');
      queryClient.invalidateQueries({ queryKey: ['urls'] });
      setAddDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to add URL');
    },
  });

  // Add bulk URLs mutation
  const addBulkMutation = useMutation({
    mutationFn: urlsApi.addBulkUrls,
    onSuccess: (response) => {
      toast.success(`Added ${response.successful} URLs (${response.failed} failed)`);
      queryClient.invalidateQueries({ queryKey: ['urls'] });
      setAddDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to add URLs');
    },
  });

  // Import sitemap mutation
  const importSitemapMutation = useMutation({
    mutationFn: urlsApi.importSitemap,
    onSuccess: (response) => {
      toast.success(`Imported ${response.added} URLs from sitemap`);
      queryClient.invalidateQueries({ queryKey: ['urls'] });
      setAddDialogOpen(false);
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to import sitemap');
    },
  });

  // Refresh URL mutation
  const refreshMutation = useMutation({
    mutationFn: (urlId: string) => {
      setRefreshingUrlId(urlId);
      return urlsApi.refreshUrl(urlId);
    },
    onSuccess: (response) => {
      if (response.success) {
        toast.success(response.contentChanged ? 'URL crawled and indexed' : 'URL crawled (no changes)');
      } else {
        toast.error(response.error || 'Crawl failed');
      }
      queryClient.invalidateQueries({ queryKey: ['urls'] });
      setRefreshingUrlId(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to refresh URL');
      setRefreshingUrlId(null);
    },
  });

  // Delete URL mutation
  const deleteMutation = useMutation({
    mutationFn: urlsApi.deleteUrl,
    onSuccess: () => {
      toast.success('URL deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['urls'] });
      setDeleteDialogOpen(false);
      setSelectedUrl(null);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete URL');
    },
  });

  // Crawl all pending URLs mutation
  const crawlAllPendingMutation = useMutation({
    mutationFn: () => {
      // Track all pending URLs as being crawled
      const pendingIds = data?.urls.filter(u => u.status === 'pending').map(u => u.id) || [];
      setCrawlingUrlIds(new Set(pendingIds));
      return urlsApi.crawlAllPending();
    },
    onSuccess: (response) => {
      if (response.queued > 0) {
        toast.success(`Started crawling ${response.queued} URLs. Watch them update below!`);
        // Start polling to see live updates
        setIsCrawlingAll(true);
      } else {
        toast.info('No pending URLs to crawl');
      }
      // Refresh the list to show "crawling" status
      queryClient.invalidateQueries({ queryKey: ['urls'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to crawl pending URLs');
      setIsCrawlingAll(false);
      setCrawlingUrlIds(new Set());
    },
  });

  // Reset stuck URLs mutation
  const resetStuckMutation = useMutation({
    mutationFn: urlsApi.resetStuckUrls,
    onSuccess: (response) => {
      toast.success(response.message);
      setIsCrawlingAll(false);
      queryClient.invalidateQueries({ queryKey: ['urls'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to reset stuck URLs');
    },
  });

  // Crawl selected URLs mutation
  const crawlSelectedMutation = useMutation({
    mutationFn: (urlIds: string[]) => {
      // Track which URLs are being crawled
      setCrawlingUrlIds(new Set(urlIds));
      return urlsApi.crawlSelectedUrls(urlIds);
    },
    onSuccess: (response) => {
      if (response.queued > 0) {
        toast.success(`Started crawling ${response.queued} selected URLs`);
        setIsCrawlingAll(true);
        setSelectedIds(new Set()); // Clear selection after starting
      } else {
        toast.info('No URLs to crawl');
        setCrawlingUrlIds(new Set());
      }
      queryClient.invalidateQueries({ queryKey: ['urls'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to crawl selected URLs');
      setCrawlingUrlIds(new Set());
    },
  });

  // Bulk delete mutation
  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      // Delete URLs one by one
      const results = await Promise.allSettled(
        ids.map(id => urlsApi.deleteUrl(id))
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;
      return { succeeded, failed };
    },
    onSuccess: ({ succeeded, failed }) => {
      if (failed === 0) {
        toast.success(`Deleted ${succeeded} URLs successfully`);
      } else {
        toast.warning(`Deleted ${succeeded} URLs, ${failed} failed`);
      }
      setSelectedIds(new Set());
      setBulkDeleteDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['urls'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to delete URLs');
    },
  });

  // Selection helpers
  const toggleSelectAll = () => {
    if (selectedIds.size === filteredUrls.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredUrls.map(u => u.id)));
    }
  };

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const resetForm = () => {
    setNewUrl('');
    setBulkUrls('');
    setSitemapUrl('');
    setCrawlFrequency('manual');
    setMaxUrls(100);
    setIncludePattern('');
    setExcludePattern('');
    setExtractMainContent(true);
    setRespectRobotsTxt(true);
    setIncludeImages(true);
  };

  const handleAddSingleUrl = () => {
    if (!newUrl.trim()) {
      toast.error('Please enter a URL');
      return;
    }
    addUrlMutation.mutate({
      url: newUrl.trim(),
      crawlFrequency,
      extractMainContent,
      respectRobotsTxt,
      includeImages,
    });
  };

  const handleAddBulkUrls = () => {
    const urls = bulkUrls
      .split('\n')
      .map(u => u.trim())
      .filter(u => u.length > 0);

    if (urls.length === 0) {
      toast.error('Please enter at least one URL');
      return;
    }

    addBulkMutation.mutate({
      urls,
      crawlFrequency,
    });
  };

  const handleImportSitemap = () => {
    if (!sitemapUrl.trim()) {
      toast.error('Please enter a sitemap URL');
      return;
    }
    importSitemapMutation.mutate({
      sitemapUrl: sitemapUrl.trim(),
      crawlFrequency,
      maxUrls,
      includePattern: includePattern || undefined,
      excludePattern: excludePattern || undefined,
    });
  };

  // Filter URLs by search query
  const filteredUrls = data?.urls.filter(url =>
    url.url.toLowerCase().includes(searchQuery.toLowerCase()) ||
    url.pageTitle?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    url.domain.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  // Stats
  const stats = {
    total: data?.urls.length || 0,
    indexed: data?.urls.filter(u => u.status === 'indexed').length || 0,
    pending: data?.urls.filter(u => u.status === 'pending').length || 0,
    failed: data?.urls.filter(u => u.status === 'failed').length || 0,
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <PageHeader
        icon={Globe}
        title="URL Sources"
        description="Add and manage web URLs for crawling and indexing"
        actions={
          <>
            {selectedIds.size > 0 && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => crawlSelectedMutation.mutate(Array.from(selectedIds))}
                  disabled={crawlSelectedMutation.isPending || isCrawlingAll}
                >
                  {crawlSelectedMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Crawl Selected ({selectedIds.size})
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setBulkDeleteDialogOpen(true)}
                  disabled={bulkDeleteMutation.isPending}
                >
                  {bulkDeleteMutation.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  Delete Selected ({selectedIds.size})
                </Button>
              </>
            )}
            {crawlingCount > 0 && !crawlAllPendingMutation.isPending && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => resetStuckMutation.mutate()}
                disabled={resetStuckMutation.isPending}
              >
                {resetStuckMutation.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <AlertCircle className="mr-2 h-4 w-4" />
                )}
                Reset Stuck ({crawlingCount})
              </Button>
            )}
            {(stats.pending > 0 || isCrawlingAll) && (
              <Button
                variant="outline"
                onClick={() => crawlAllPendingMutation.mutate()}
                disabled={crawlAllPendingMutation.isPending || isCrawlingAll}
              >
                {crawlAllPendingMutation.isPending || isCrawlingAll ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {isCrawlingAll
                  ? `Crawling... (${crawlingCount} remaining)`
                  : crawlAllPendingMutation.isPending
                    ? 'Starting...'
                    : `Crawl All Pending (${stats.pending})`}
              </Button>
            )}
            <Button onClick={() => setAddDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add URL
            </Button>
          </>
        }
      />


      {/* Stats Cards - Compact with Icons (matching Knowledge Base style) */}
      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <Card className="p-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Globe className="h-5 w-5 text-blue-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total URLs</p>
              <p className="text-xl font-bold">{stats.total}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-500/10">
              <CheckCircle2 className="h-5 w-5 text-green-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Indexed</p>
              <p className="text-xl font-bold text-green-600">{stats.indexed}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10">
              <Clock className="h-5 w-5 text-orange-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Pending</p>
              <p className="text-xl font-bold text-orange-600">{stats.pending}</p>
            </div>
          </div>
        </Card>
        <Card className="p-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-red-500/10">
              <XCircle className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Failed</p>
              <p className="text-xl font-bold text-red-600">{stats.failed}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Search and URLs Table */}
      <Card>
        <div className="p-4 border-b">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search URLs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="indexed">Indexed</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="crawling">Crawling</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="disabled">Disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="p-0">
          {isLoading ? (
            <div className="p-4 space-y-3">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-10 w-10 rounded" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-1/3" />
                    <Skeleton className="h-3 w-1/2" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="p-6 text-center text-muted-foreground">
              <AlertCircle className="mx-auto h-12 w-12 mb-4 text-red-500" />
              <p>Failed to load URLs</p>
            </div>
          ) : filteredUrls.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <Globe className="mx-auto h-12 w-12 mb-4 opacity-50" />
              <h3 className="text-lg font-medium">No URLs yet</h3>
              <p className="mb-4">Add URLs to start crawling and indexing web content</p>
              <Button onClick={() => setAddDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Your First URL
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={filteredUrls.length > 0 && selectedIds.size === filteredUrls.length}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="hidden lg:table-cell">Images</TableHead>
                    <TableHead className="hidden lg:table-cell">Description</TableHead>
                    <TableHead className="hidden md:table-cell">Frequency</TableHead>
                    <TableHead className="hidden lg:table-cell">Last Crawled</TableHead>
                    <TableHead className="hidden sm:table-cell">Size</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUrls.map((url) => (
                    <TableRow key={url.id} className={selectedIds.has(url.id) ? 'bg-muted/50' : ''}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIds.has(url.id)}
                          onCheckedChange={() => toggleSelect(url.id)}
                          aria-label={`Select ${url.url}`}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-start gap-3">
                          <div className="flex h-10 w-10 items-center justify-center rounded bg-muted flex-shrink-0">
                            <Globe className="h-5 w-5 text-muted-foreground" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium truncate max-w-[200px] sm:max-w-[300px]" title={url.pageTitle || url.url}>
                                {url.pageTitle || url.domain}
                              </p>
                              <a
                                href={url.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-muted-foreground hover:text-foreground flex-shrink-0"
                              >
                                <ExternalLink className="h-3 w-3" />
                              </a>
                            </div>
                            <p className="text-sm text-muted-foreground truncate max-w-[250px] sm:max-w-[350px]" title={url.url}>
                              {url.url}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={url.status} />
                        {url.lastError && (
                          <p className="text-xs text-red-500 mt-1 truncate max-w-[150px]" title={url.lastError}>
                            {url.lastError}
                          </p>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {url.includeImages ? (
                          url.status === 'indexed' ? (
                            <div className="flex items-center gap-1.5">
                              <Image className="h-4 w-4 text-green-500" />
                              <span className="text-sm text-muted-foreground">Enabled</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <Image className="h-4 w-4 text-muted-foreground" />
                              <span className="text-xs text-muted-foreground">Pending</span>
                            </div>
                          )
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {url.status === 'crawling' || refreshingUrlId === url.id || crawlingUrlIds.has(url.id) ? (
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span className="text-xs">
                              {url.status === 'crawling' ? 'Extracting...' : 'Queued...'}
                            </span>
                          </div>
                        ) : url.pageDescription ? (
                          url.pageDescription.length > 60 ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <span className="text-sm text-muted-foreground max-w-[280px] cursor-pointer hover:text-foreground line-clamp-2 block">
                                  {url.pageDescription.substring(0, 60)}...
                                </span>
                              </PopoverTrigger>
                              <PopoverContent side="top" className="w-auto max-w-[350px] p-3">
                                <p className="text-sm">{url.pageDescription}</p>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span className="text-sm text-muted-foreground max-w-[280px] line-clamp-2 block">
                              {url.pageDescription}
                            </span>
                          )
                        ) : (
                          <span className="text-xs text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <FrequencyBadge frequency={url.crawlFrequency} />
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {url.lastCrawlAt ? (
                          <span className="text-sm text-muted-foreground">
                            {formatDistanceToNow(new Date(url.lastCrawlAt), { addSuffix: true })}
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">Never</span>
                        )}
                      </TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <span className="text-sm text-muted-foreground">
                          {formatBytes(url.contentLengthBytes)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-8 w-8">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => refreshMutation.mutate(url.id)}
                              disabled={refreshingUrlId !== null}
                            >
                              {refreshingUrlId === url.id ? (
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              ) : (
                                <RefreshCw className="mr-2 h-4 w-4" />
                              )}
                              {refreshingUrlId === url.id ? 'Crawling...' : 'Crawl Now'}
                            </DropdownMenuItem>
                            <DropdownMenuItem asChild>
                              <a href={url.url} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="mr-2 h-4 w-4" />
                                Open URL
                              </a>
                            </DropdownMenuItem>
                            {url.status === 'indexed' && (
                              <>
                                <DropdownMenuItem
                                  onClick={async () => {
                                    try {
                                      const response = await urlsApi.getMarkdownUrl(url.id);
                                      if (response.success && response.url) {
                                        window.open(response.url, '_blank');
                                      } else {
                                        toast.error('No markdown available. Please re-crawl this URL.');
                                      }
                                    } catch (error: any) {
                                      if (error?.response?.status === 404) {
                                        toast.error('No markdown available. Please re-crawl this URL to generate markdown.');
                                      } else {
                                        toast.error('Failed to get markdown URL');
                                      }
                                    }
                                  }}
                                >
                                  <FileCode className="mr-2 h-4 w-4" />
                                  View Markdown
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={async () => {
                                    try {
                                      const response = await urlsApi.getMarkdownUrl(url.id);
                                      if (response.success && response.url) {
                                        // Create a download link
                                        const link = document.createElement('a');
                                        link.href = response.url;
                                        link.download = `${url.pageTitle || url.domain}.md`;
                                        document.body.appendChild(link);
                                        link.click();
                                        document.body.removeChild(link);
                                      } else {
                                        toast.error('No markdown available. Please re-crawl this URL.');
                                      }
                                    } catch (error: any) {
                                      if (error?.response?.status === 404) {
                                        toast.error('No markdown available. Please re-crawl this URL to generate markdown.');
                                      } else {
                                        toast.error('Failed to download markdown');
                                      }
                                    }
                                  }}
                                >
                                  <FileText className="mr-2 h-4 w-4" />
                                  Download Markdown
                                </DropdownMenuItem>
                              </>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => {
                                setSelectedUrl(url);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </Card>

      {/* Add URL Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add URL</DialogTitle>
            <DialogDescription>
              Add a web URL to crawl and index for the knowledge base
            </DialogDescription>
          </DialogHeader>

          <Tabs value={addTab} onValueChange={(v) => setAddTab(v as any)}>
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="single">
                <Link className="mr-2 h-4 w-4" />
                Single
              </TabsTrigger>
              <TabsTrigger value="bulk">
                <Link2 className="mr-2 h-4 w-4" />
                Bulk
              </TabsTrigger>
              <TabsTrigger value="sitemap">
                <Map className="mr-2 h-4 w-4" />
                Sitemap
              </TabsTrigger>
            </TabsList>

            <TabsContent value="single" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="url">URL</Label>
                <Input
                  id="url"
                  placeholder="https://example.com/page"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Crawl Frequency</Label>
                <Select value={crawlFrequency} onValueChange={(v: any) => setCrawlFrequency(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual only</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="extractMain">Extract main content only</Label>
                <Switch
                  id="extractMain"
                  checked={extractMainContent}
                  onCheckedChange={setExtractMainContent}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="includeImages">Include images</Label>
                <Switch
                  id="includeImages"
                  checked={includeImages}
                  onCheckedChange={setIncludeImages}
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="robotsTxt">Respect robots.txt</Label>
                <Switch
                  id="robotsTxt"
                  checked={respectRobotsTxt}
                  onCheckedChange={setRespectRobotsTxt}
                />
              </div>
            </TabsContent>

            <TabsContent value="bulk" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>URLs (one per line)</Label>
                <Textarea
                  placeholder="https://example.com/page1&#10;https://example.com/page2&#10;https://example.com/page3"
                  value={bulkUrls}
                  onChange={(e) => setBulkUrls(e.target.value)}
                  rows={6}
                />
                <p className="text-xs text-muted-foreground">
                  Enter up to 100 URLs, one per line
                </p>
              </div>
              <div className="space-y-2">
                <Label>Crawl Frequency</Label>
                <Select value={crawlFrequency} onValueChange={(v: any) => setCrawlFrequency(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual only</SelectItem>
                    <SelectItem value="daily">Daily</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="sitemap" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Sitemap URL</Label>
                <Input
                  placeholder="https://example.com/sitemap.xml"
                  value={sitemapUrl}
                  onChange={(e) => setSitemapUrl(e.target.value)}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Max URLs</Label>
                  <Input
                    type="number"
                    min={1}
                    max={500}
                    value={maxUrls}
                    onChange={(e) => setMaxUrls(parseInt(e.target.value) || 100)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Crawl Frequency</Label>
                  <Select value={crawlFrequency} onValueChange={(v: any) => setCrawlFrequency(v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">Manual only</SelectItem>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Include Pattern (optional regex)</Label>
                <Input
                  placeholder="/blog/.*"
                  value={includePattern}
                  onChange={(e) => setIncludePattern(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Exclude Pattern (optional regex)</Label>
                <Input
                  placeholder="/archive/.*"
                  value={excludePattern}
                  onChange={(e) => setExcludePattern(e.target.value)}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (addTab === 'single') handleAddSingleUrl();
                else if (addTab === 'bulk') handleAddBulkUrls();
                else handleImportSitemap();
              }}
              disabled={addUrlMutation.isPending || addBulkMutation.isPending || importSitemapMutation.isPending}
            >
              {(addUrlMutation.isPending || addBulkMutation.isPending || importSitemapMutation.isPending) && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {addTab === 'sitemap' ? 'Import' : 'Add'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete URL</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this URL? This will also remove all associated content from the knowledge base.
              {selectedUrl && (
                <span className="block mt-2 font-medium text-foreground">
                  {selectedUrl.url}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => selectedUrl && deleteMutation.mutate(selectedUrl.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {selectedIds.size} URLs</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedIds.size} selected URLs? This will also remove all associated content from the knowledge base. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => bulkDeleteMutation.mutate(Array.from(selectedIds))}
              className="bg-red-600 hover:bg-red-700"
            >
              {bulkDeleteMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : null}
              Delete {selectedIds.size} URLs
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default UrlSourcesPage;
